// The one post composer (D14), used by the Home feed and Creator studio. Presentational: the caller
// passes `onPublish` (a promise); the draft clears only when it resolves, and stays on failure.
import {
  useId,
  useState,
  type FormEvent,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { Link2, X } from 'lucide-react';
import { Avatar } from './Avatar';
import { Switch } from './Field';
import { cx } from './cx';

export type ComposerVisibility = 'public' | 'connections' | 'private';

export interface ComposerDraft {
  body: string;
  linkUrl: string | null;
  visibility: ComposerVisibility;
  seoEnabled: boolean;
  geoEnabled: boolean;
}

export const COMPOSER_MAX = 3000;

export function checkLink(raw: string): { ok: true; url: string | null } | { ok: false; error: string } {
  const v = raw.trim();
  if (!v) return { ok: true, url: null };
  try {
    const u = new URL(v);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') throw new Error('protocol');
    return { ok: true, url: u.toString() };
  } catch {
    return { ok: false, error: 'Enter a full address starting with https://' };
  }
}

export function Composer({
  author,
  onPublish,
  heading,
  placeholder = 'Share something with your network',
  textareaId,
  className,
  body: controlledBody,
  onBodyChange,
  textareaRef,
  textareaProps,
  children,
  tools,
  footer,
  publishLabel = 'Publish',
  blockedReason = null,
}: {
  author: { name: string; avatarUrl: string | null };
  onPublish: (draft: ComposerDraft) => Promise<unknown>;
  /** Optional visible heading (Creator studio); the feed composer relies on the textarea label. */
  heading?: string;
  placeholder?: string;
  /** Stable id so keyboard shortcuts (N) can focus the composer. */
  textareaId?: string;
  className?: string;
  // Optional extensions (Home feed: images, @mentions, scheduling). All optional and backward compatible.
  /** Controlled body; pair with `onBodyChange`. Uncontrolled (internal state) when omitted. */
  body?: string;
  onBodyChange?: (body: string) => void;
  textareaRef?: Ref<HTMLTextAreaElement>;
  /** Extra textarea attributes (aria-*, onKeyDown, onSelect...). An onKeyDown that calls preventDefault() suppresses Ctrl+Enter publish. */
  textareaProps?: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'value' | 'onChange' | 'maxLength'>;
  /** Rendered under the textarea (autocomplete list, attachment previews). */
  children?: ReactNode;
  /** Extra toolbar buttons next to "Add link". */
  tools?: ReactNode;
  /** Rendered under the toolbar (e.g. a schedule picker). */
  footer?: ReactNode;
  publishLabel?: string;
  /** When set, Publish stays disabled and this reason is shown under the toolbar. */
  blockedReason?: string | null;
}) {
  const auto = useId();
  const tid = textareaId ?? `${auto}-body`;
  const [ownBody, setOwnBody] = useState('');
  const body = controlledBody ?? ownBody;
  const setBody = (v: string) => {
    if (controlledBody === undefined) setOwnBody(v);
    onBodyChange?.(v);
  };
  const [link, setLink] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [visibility, setVisibility] = useState<ComposerVisibility>('public');
  const [seo, setSeo] = useState(true);
  const [geo, setGeo] = useState(true);
  const [pending, setPending] = useState(false);
  const linkCheck = checkLink(link);
  const len = body.trim().length;
  const crawlable = visibility === 'public';
  const canPublish = len > 0 && len <= COMPOSER_MAX && linkCheck.ok && !pending && !blockedReason;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canPublish || !linkCheck.ok) return;
    setPending(true);
    try {
      await onPublish({
        body: body.trim(),
        linkUrl: linkCheck.url,
        visibility,
        seoEnabled: crawlable && seo,
        geoEnabled: crawlable && geo,
      });
      setBody('');
      setLink('');
      setShowLink(false);
    } catch {
      // The caller reports the failure (toast); the draft is kept so nothing is lost.
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className={cx('fl-card flex flex-col gap-3', className)}
      aria-label={heading ?? 'New post'}
    >
      {heading ? <h2 className="text-xl">{heading}</h2> : null}
      <div className="flex gap-3">
        <Avatar name={author.name} src={author.avatarUrl} size={44} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor={tid} className="sr-only">
            Write a post
          </label>
          <textarea
            placeholder={placeholder}
            {...textareaProps}
            ref={textareaRef}
            id={tid}
            rows={body ? 4 : 2}
            maxLength={COMPOSER_MAX}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              textareaProps?.onKeyDown?.(e);
              if (e.defaultPrevented) return;
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit(e);
            }}
            className={cx(
              'fl-input min-h-[3.25rem] resize-y border-transparent bg-paper-2 text-[1.0625rem] leading-relaxed focus-visible:bg-sheet',
              textareaProps?.className,
            )}
          />
          {children}
        </div>
      </div>
      {showLink ? (
        <div className="flex items-end gap-2 sm:pl-14">
          <div className="fl-field flex-1">
            <label className="fl-label" htmlFor={`${auto}-link`}>
              Link
            </label>
            <input
              id={`${auto}-link`}
              type="url"
              inputMode="url"
              placeholder="https://"
              className="fl-input"
              value={link}
              aria-invalid={linkCheck.ok ? undefined : true}
              aria-describedby={linkCheck.ok ? undefined : `${auto}-link-err`}
              onChange={(e) => setLink(e.target.value)}
            />
            {!linkCheck.ok ? (
              <span id={`${auto}-link-err`} className="fl-error">
                {linkCheck.error}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="fl-btn"
            data-variant="ghost"
            data-icon-only="true"
            aria-label="Remove link"
            onClick={() => {
              setLink('');
              setShowLink(false);
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:pl-14">
        {!showLink ? (
          <button type="button" className="fl-btn px-2.5" data-variant="ghost" data-size="sm" onClick={() => setShowLink(true)}>
            <Link2 size={16} aria-hidden="true" />
            Add link
          </button>
        ) : null}
        {tools}
        <label htmlFor={`${auto}-vis`} className="sr-only">
          Who can see it
        </label>
        <select
          id={`${auto}-vis`}
          className="fl-input w-auto min-h-9 border-transparent bg-paper-2 py-1 text-sm font-medium"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as ComposerVisibility)}
        >
          <option value="public">Anyone</option>
          <option value="connections">Connections</option>
          <option value="private">Only me</option>
        </select>
        <div className="ml-auto flex items-center gap-3">
          <span className={cx('text-xs tabular', len > COMPOSER_MAX * 0.9 ? 'text-stop' : 'text-ink-3')}>
            {len > 0 ? `${len}/${COMPOSER_MAX}` : null}
          </span>
          <button
            type="submit"
            className="fl-btn"
            data-variant="primary"
            disabled={!canPublish}
            aria-busy={pending || undefined}
            aria-describedby={blockedReason ? `${auto}-blocked` : undefined}
          >
            {pending ? <span className="fl-spinner" aria-hidden="true" /> : null}
            {publishLabel}
          </button>
        </div>
      </div>
      {blockedReason ? (
        <p id={`${auto}-blocked`} className="m-0 text-sm text-ink-2 sm:pl-14">
          {blockedReason}
        </p>
      ) : null}
      {footer}
      {body ? (
        <fieldset className="m-0 flex flex-wrap items-center gap-x-5 gap-y-1 border-0 border-hair-t p-0 pt-2 sm:pl-14">
          <legend className="sr-only">Discoverable by</legend>
          <span className="text-sm text-ink-2" aria-hidden="true">
            Discoverable by
          </span>
          <Switch size="sm" label="Search engines" checked={crawlable && seo} disabled={!crawlable} onChange={setSeo} />
          <Switch size="sm" label="AI assistants" checked={crawlable && geo} disabled={!crawlable} onChange={setGeo} />
        </fieldset>
      ) : null}
      {!crawlable ? (
        <p className="text-sm text-ink-2 sm:pl-14">
          Only public posts get a public page, so search engines and AI assistants will not see this one.
        </p>
      ) : null}
    </form>
  );
}
